(() => {
  let queued = false;
  const requests = new Map();
  const pending = new WeakSet();
  const failed = new WeakSet();
  const accountBindings = new WeakSet();
  let activeRequests = 0;
  const waitingRequests = [];
  let discoveryRequest;
  const discoveryStates = new WeakMap();

  function setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  function wishlistCount(page) {
    const products = new Set();
    page.querySelectorAll('wishlist-product-card').forEach((card, index) => {
      const link = card.querySelector('.wk-image-link');
      products.add(link ? new URL(link.href, location.origin).pathname : `card-${index}`);
    });
    return products.size;
  }

  function wishlistHandles(page) {
    const handles = new Set();
    page.querySelectorAll('wishlist-product-card .wk-image-link').forEach(link => {
      const url = new URL(link.href, location.origin);
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      if (url.origin === location.origin && match) handles.add(match[1]);
    });
    return handles;
  }

  async function syncDiscovery(page) {
    const discovery = document.querySelector('[data-nw-discovery]');
    const grid = discovery?.querySelector('.nw-discovery__grid');
    const pool = discovery?.querySelector('[data-nw-discovery-pool]');
    if (!grid || !pool) return;
    let state = discoveryStates.get(discovery);
    if (!state) {
      let handles;
      try { handles = JSON.parse(pool.textContent); } catch { return; }
      if (!Array.isArray(handles)) return;
      state = { handles: [...new Set(handles.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle)))], cards: new Map(), failed: new Set(), signature: null, version: 0 };
      grid.querySelectorAll(':scope > product-card[handle]').forEach(card => state.cards.set(card.getAttribute('handle'), card));
      discoveryStates.set(discovery, state);
    }
    const saved = wishlistHandles(page);
    const signature = [...saved].sort().join(',');
    if (state.signature === signature) return;
    state.signature = signature;
    const version = ++state.version;
    const desired = state.handles.filter(handle => !saved.has(handle) && !state.failed.has(handle)).slice(0, 4);
    const focusRemoved = [...grid.querySelectorAll(':scope > product-card')].some(card => !desired.includes(card.getAttribute('handle')) && card.contains(document.activeElement));
    grid.querySelectorAll(':scope > product-card').forEach(card => {
      if (!desired.includes(card.getAttribute('handle'))) card.remove();
    });
    grid.setAttribute('aria-busy', 'true');
    const root = window.Shopify?.routes?.root || '/';
    const cards = await Promise.all(desired.map(async handle => {
      if (state.cards.has(handle)) return state.cards.get(handle);
      try {
        const parsed = await getCard(cardUrl({ href: new URL(root + 'products/' + handle, location.origin).href }));
        const card = document.importNode(parsed.querySelector('product-card'), true);
        state.cards.set(handle, card);
        return card;
      } catch (error) {
        state.failed.add(handle);
        return null;
      }
    }));
    if (version !== state.version || !page.isConnected || !discovery.isConnected) return;
    // A wishlist update can land while a replacement product is loading.
    if ([...wishlistHandles(page)].sort().join(',') !== signature) {
      state.signature = null;
      return syncDiscovery(page);
    }
    cards.filter(Boolean).forEach((card, index) => {
      const current = grid.querySelectorAll(':scope > product-card')[index];
      if (current !== card) grid.insertBefore(card, current || null);
    });
    grid.setAttribute('aria-busy', 'false');
    const empty = discovery.querySelector('[data-nw-discovery-empty]');
    if (empty) empty.hidden = grid.querySelectorAll(':scope > product-card').length > 0;
    window.productCardQuickSizeInstance?.init();
    if (focusRemoved && document.activeElement === document.body) {
      grid.querySelector('wishlist-button-collection button')?.focus({ preventScroll: true });
    }
    if (cards.some(card => !card) && state.failed.size < 8) {
      state.signature = null;
      return syncDiscovery(page);
    }
  }

  function updateCampaign(page) {
    const count = wishlistCount(page);
    const authenticated = document.getElementById('niihai-wishlist-card-config')?.dataset.authenticated === 'true';
    page.dataset.nwEmpty = String(count === 0);
    const progress = page.querySelector('[data-nw-progress]');
    if (progress) {
      progress.setAttribute('aria-valuenow', String(Math.min(count, 3)));
      progress.querySelectorAll('span').forEach((segment, index) => segment.classList.toggle('is-filled', index < count));
    }
    setText(page.querySelector('[data-nw-progress-label]'), count >= 3 ? window.niihaiT("niihai.custom.cb281eee1671b","{count} pieces added").replace("{count}", String(count)) : window.niihaiT("niihai.custom.ca4b5d7a250b0","{count} of 3 pieces added").replace("{count}", String(count)));
    setText(page.querySelector('[data-nw-save-copy]'), count >= 3
      ? (authenticated ? window.niihaiT("niihai.custom.cd54d27253458","Your wishlist is ready for the draw. Keep your favourites saved to your account.") : window.niihaiT("niihai.custom.cf84c4bf308f4","Your favourites are ready. Save your wishlist to enter the draw."))
      : (authenticated ? window.niihaiT("niihai.custom.c1bda97ad61f5","Add 3 or more pieces to your saved wishlist to enter the draw.") : window.niihaiT("niihai.custom.c3a3c764deb63","Add 3 or more pieces, then save your wishlist to enter the draw.")));
    const discovery = document.querySelector('[data-nw-discovery]');
    setText(discovery?.querySelector('#nw-discovery-title'), count === 0 ? window.niihaiT("niihai.custom.c7c1d48ac27af","Start with the bestsellers") : window.niihaiT("niihai.custom.c3ef2f76e2ba9","More to love"));
    setText(discovery?.querySelector('[data-nw-discovery-copy]'), count === 0
      ? window.niihaiT("niihai.custom.c65c247bdebfe","Your wishlist starts here. Find a favourite and make it yours.")
      : window.niihaiT("niihai.custom.ceb4b5e21343f","Keep building your wishlist with our most-loved pieces."));
    syncDiscovery(page);
  }

  async function loadDiscovery(page) {
    if (discoveryRequest) return discoveryRequest;
    discoveryRequest = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(root + 'collections/bestsellers', location.origin);
      url.searchParams.set('section_id', 'niihai-wishlist-discovery');
      const preview = document.getElementById('niihai-wishlist-card-config')?.dataset.previewThemeId;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Wishlist discovery unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const discovery = parsed.querySelector('[data-nw-discovery]');
        if (!discovery || !page.isConnected) return;
        page.after(document.importNode(discovery, true));
        window.productCardQuickSizeInstance?.init();
        updateCampaign(page);
      } catch (error) {
        console.warn('[wishlist] Keeping the collection link as discovery fallback', error);
        if (!page.isConnected) return;
        const fallback = document.createElement('div');
        fallback.className = 'nw-discovery__fallback';
        const link = document.createElement('a');
        link.href = root + 'collections/bestsellers';
        link.textContent = window.niihaiT("niihai.custom.ccd4bcaa2efd6","Discover the bestsellers");
        fallback.append(link);
        page.after(fallback);
      } finally {
        clearTimeout(timer);
      }
    })();
    return discoveryRequest;
  }

  async function takeRequestSlot() {
    if (activeRequests < 4) { activeRequests++; return; }
    await new Promise(resolve => waitingRequests.push(resolve));
  }
  function releaseRequestSlot() {
    const next = waitingRequests.shift();
    if (next) next();
    else activeRequests--;
  }

  async function refreshAfterSignIn() {
    // Refresh only after authentication so Swish receives the signed-in Liquid customer context.
    const url = new URL(location.href);
    url.searchParams.set('section_id', 'niihai-wishlist-card');
    const preview = document.getElementById('niihai-wishlist-card-config')?.dataset.previewThemeId;
    if (preview) url.searchParams.set('preview_theme_id', preview);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (parsed.querySelector('[data-nw-authenticated]')?.getAttribute('data-nw-authenticated') === 'true') location.reload();
    } catch (error) {
      console.warn('[wishlist] Account context check unavailable', error);
    }
  }

  function cardUrl(link) {
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return null;
    const root = window.Shopify?.routes?.root || '/';
    if (root !== '/' && url.pathname.startsWith('/products/')) url.pathname = root.replace(/\/$/, '') + url.pathname;
    url.search = '';
    url.searchParams.set('section_id', 'niihai-wishlist-card');
    const preview = document.getElementById('niihai-wishlist-card-config')?.dataset.previewThemeId;
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  function getCard(url) {
    const key = url.href;
    if (!requests.has(key)) {
      requests.set(key, (async () => {
        await takeRequestSlot();
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error('Product card unavailable');
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          if (!parsed.querySelector('product-card')) throw new Error('Product card missing');
          return parsed;
        } finally {
          clearTimeout(timer);
          releaseRequestSlot();
        }
      })());
    }
    return requests.get(key);
  }

  async function enhanceCard(card) {
    if (card.classList.contains('nw-liquid-card') || pending.has(card) || failed.has(card)) return;
    const link = card.querySelector('.wk-image-link');
    if (!link) return;
    const url = cardUrl(link);
    if (!url) return;
    pending.add(card);
    try {
      const parsed = await getCard(url);
      if (!card.isConnected) return;
      if (!document.getElementById('niihai-wishlist-card-style')) {
        const style = parsed.querySelector('style');
        if (style) {
          const sharedStyle = style.cloneNode(true);
          sharedStyle.id = 'niihai-wishlist-card-style';
          document.head.append(sharedStyle);
        }
      }
      const productCard = document.importNode(parsed.querySelector('product-card'), true);
      const remove = card.querySelector('remove-button');
      if (remove) productCard.querySelector('.product-card__figure')?.append(remove);
      card.prepend(productCard);
      card.classList.add('nw-liquid-card');
      // This is the same controller used by collection cards, not a second quick-add implementation.
      window.productCardQuickSizeInstance?.init();
    } catch (error) {
      failed.add(card);
      console.warn('[wishlist] Keeping the app card as a fallback', error);
    } finally {
      pending.delete(card);
    }
  }

  function enhance() {
    const page = document.querySelector('#main > wishlist-page');
    if (!page) return;
    const header = page.querySelector('.wk-header');
    const personalWishlist = !page.dataset.wishlistId || page.dataset.wishlistId === 'mine';
    if (!personalWishlist) {
      page.querySelectorAll('wishlist-product-card').forEach(enhanceCard);
      return;
    }
    const campaignTemplate = document.getElementById('niihai-wishlist-campaign-template');
    if (header && campaignTemplate && !document.querySelector('[data-nw-campaign]')) page.before(campaignTemplate.content.cloneNode(true));
    const title = header?.querySelector('.wk-title');
    if (title) {
      title.setAttribute('role', 'heading');
      title.setAttribute('aria-level', '2');
      setText(title, window.niihaiT("niihai.custom.ce0b4d18e7a96","Your wishlist"));
    }
    const saveTemplate = document.getElementById('niihai-wishlist-save-template');
    if (header && saveTemplate && !page.querySelector('[data-nw-save]')) header.before(saveTemplate.content.cloneNode(true));
    const account = page.querySelector('.nw-account');
    if (account && !accountBindings.has(account)) {
      account.addEventListener('close', refreshAfterSignIn);
      accountBindings.add(account);
    }
    if (header) {
      updateCampaign(page);
      loadDiscovery(page);
    }
    page.querySelectorAll('wishlist-product-card').forEach(enhanceCard);
  }
  function scheduleEnhance() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; enhance(); });
  }
  const main = document.getElementById('main');
  if (main) new MutationObserver(scheduleEnhance).observe(main, { childList: true, subtree: true });
  enhance();
})();
